import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { PolicyBlock, PolicyListItem } from "../model/PolicyBlock";
import { PolicyType, getPolicyDocument } from "../model/PolicyDocument";
import styles from "../css/PolicyScreen.module.css";

interface PolicyScreenProps {
  type: PolicyType;
}

function PolicyListItems(props: { items: PolicyListItem[]; ordered: boolean }) {
  const ListTag = props.ordered === true ? "ol" : "ul";

  return (
    <ListTag className={props.ordered === true ? styles.orderedList : styles.bulletedList}>
      {props.items.map((item, index) => (
        <li className={styles.listItem} key={index}>
          {item.text}
          {item.children !== undefined && item.children.length > 0 ? (
            <PolicyListItems items={item.children} ordered={props.ordered} />
          ) : null}
        </li>
      ))}
    </ListTag>
  );
}

function PolicyScreen(props: PolicyScreenProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const document = getPolicyDocument(props.type, i18n.language);
  const title = props.type === "privacy" ? t("PrivacyPolicy") : t("TermsOfUse");

  function handleClickBackButton() {
    navigate("/");
  }

  function renderBlock(block: PolicyBlock, index: number) {
    switch (block.kind) {
      case "heading":
        return (
          <h2 className={styles.heading} key={index}>
            {block.text}
          </h2>
        );
      case "paragraph":
        return (
          <p className={styles.paragraph} key={index}>
            {block.text}
          </p>
        );
      case "list":
        return (
          <PolicyListItems
            items={block.items}
            ordered={block.ordered}
            key={index}
          />
        );
    }
  }

  return (
    <div className={styles.policyScreen}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <button
            className={styles.backButton}
            onClick={() => handleClickBackButton()}
            aria-label={t("PolicyBack")}
          >
            <ArrowBackRoundedIcon fontSize="inherit" />
          </button>
          <img
            className={styles.headerLogo}
            src="/imgOnevocaLogoBlue.png"
            alt="Onevoca"
          />
        </div>
      </header>

      <main className={styles.content}>
        <h1 className={styles.title}>{title}</h1>
        {document.isKoreanFallback === true ? (
          <p className={styles.fallbackNotice}>{t("PolicyKoreanOnlyNotice")}</p>
        ) : null}
        {document.blocks.map((block, index) => renderBlock(block, index))}
      </main>
    </div>
  );
}

export default PolicyScreen;
